import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  useAddItemMutation,
  useFetchItem,
  useFetchItems,
  useUpdateItemMutation,
} from './base-hooks';
import { CacheKey } from '../types/cache-keys';
import { CustomTile, CustomTileCategory, DashboardPlacement, DashboardTileLink, LeaderPage, SystemBanner } from '../types/dashboard';
import { fetchItems } from '../support/queries';
import { useMemo } from 'react';
import { TileDataSource, TileDataSourceEntity } from '../types/customTile';


// Join table: Solution Component <-> Product Feature


// Custom Tile
const useFetchSingleCustomTile = (id, params?, options?) =>
  useFetchItem<CustomTile>('db/custom-tile', id, CacheKey.CustomTile, params, options);

const useFetchCustomTiles = (params?, options?) =>
  useFetchItems<CustomTile[]>('db/custom-tile', CacheKey.CustomTile, params, options);

const useFetchCustomTileCategories = (params?, options?) =>
  useFetchItems<CustomTileCategory[]>('db/custom-tile-category', CacheKey.CustomTileCategory, params, options);

// Custom Tile View

const useFetchCustomTileViews = (params?, options?) =>
  useFetchItems('db/custom-tile-view', CacheKey.CustomTileView, params, options);

const useFetchIntegrationModules = (params?, options?) =>
  useFetchItems('db/integration-module', CacheKey.IntegrationModule, params, options);

// -------------------- Dashboard Layout (UPDATED WITH PROPER CACHE KEYS) --------------------


// ✅ FIX: Custom implementation with params in cache key
const useFetchDashboardPlacements = (
  params?: {
    component_key?: string;
    region_key?: string;
    enabled?: boolean;
    [key: string]: any;
  },
  options?: any
) => {
  // Build a stable cache key that includes filter params
  const cacheKey = [
    CacheKey.DashboardPlacement,
    params?.component_key || 'all',
    params?.region_key || 'all',
    params?.enabled !== undefined ? String(params.enabled) : 'all',
  ];

  return useQuery<DashboardPlacement[]>({
    queryKey: cacheKey,
    queryFn: () => fetchItems('db/dashboard-placement', params || {}),
    ...{
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        ...options,
      },
  });
};

// ✅ FIX: Custom implementation with params in cache key
const useFetchDashboardTileLinks = (
  params?: {
    placement?: number;
    tile?: number;
    enabled?: boolean;
    order_by?: string;
    /**
     * Allow callers to override these, but we will provide defaults below.
     * Your serializer only returns nested `tile` when detailed=true and depth>0.
     */
    detailed?: boolean;
    depth?: number;
    [key: string]: any;
  },
  options?: any,
) => {
  // Defaults to ensure DashboardTileLinkSerializer includes nested `tile`
  const effectiveParams = {
    ...(params || {}),
    detailed: params?.detailed ?? true,
    depth: params?.depth ?? 1,
    // Optional pagination defaults to be safe/consistent with your endpoints
    page: params?.page ?? 1,
    size: params?.size ?? 1000,
  };

  // Build a stable cache key that includes filter params + nesting controls
  const cacheKey = [
    CacheKey.DashboardTileLink,
    effectiveParams.placement ? `placement-${effectiveParams.placement}` : 'all',
    effectiveParams.tile ? `tile-${effectiveParams.tile}` : 'all',
    effectiveParams.enabled !== undefined ? String(effectiveParams.enabled) : 'all',
    effectiveParams.order_by || 'default',
    `detailed-${String(effectiveParams.detailed)}`,
    `depth-${String(effectiveParams.depth)}`,
    `page-${String(effectiveParams.page)}`,
    `size-${String(effectiveParams.size)}`,
  ];

  return useQuery<DashboardTileLink[]>({
    queryKey: cacheKey,
    queryFn: () => fetchItems('db/dashboard-tile-link', effectiveParams),
    ...{
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        ...options,
      },
  });
};

// Leader page definitions (domain tabs + per-mode render config; see support/leaderPages.ts).
// Fetched once at Leader-dashboard mount; the routing is derived client-side from these rows,
// with a built-in default map used when the table is empty (pre-seed safety).
const useFetchLeaderPages = (
  params?: { domain?: string; mode?: string; enabled?: boolean; [key: string]: any },
  options?: any,
) => {
  const effectiveParams = { enabled: true, order_by: 'order', size: 1000, ...(params || {}) };
  const cacheKey = [
    CacheKey.LeaderPage,
    effectiveParams.domain || 'all',
    effectiveParams.mode || 'all',
    effectiveParams.enabled !== undefined ? String(effectiveParams.enabled) : 'all',
  ];
  return useQuery<LeaderPage[]>({
    queryKey: cacheKey,
    queryFn: () => fetchItems('db/leader-page', effectiveParams),
    ...{
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        ...options,
      },
  });
};

// System banners (db/models/common.py SystemBanner). Admin-managed landing-page notifications.
// Fetched active-only, ordered; the window (starts_at/ends_at) and per-user dismiss are applied
// client-side by the <SystemBanners> component. Every authenticated user holds api.system-banner.read.
const useFetchSystemBanners = (
  params?: { active?: boolean; [key: string]: any },
  options?: any,
) => {
  const effectiveParams = { active: true, order_by: 'order', size: 1000, ...(params || {}) };
  const cacheKey = [
    CacheKey.SystemBanner,
    effectiveParams.active !== undefined ? String(effectiveParams.active) : 'all',
  ];
  return useQuery<SystemBanner[]>({
    queryKey: cacheKey,
    queryFn: () => fetchItems('db/system-banner', effectiveParams),
    ...{
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        ...options,
      },
  });
};

// Vendor

const useFetchVendors = (params?, options?) =>
  useFetchItems('db/vendor', CacheKey.Vendor, params, options);





// ProductComponent <-> Resource (NEW)
const useFetchProductComponentToResources = (params?, options?) =>
  useFetchItems('db/product-component-to-resource', CacheKey.ProductComponentToResource, params, options);

// ProductUseCase <-> Resource (NEW)
const useFetchProductUseCaseToResources = (params?, options?) =>
  useFetchItems('db/product-use-case-to-resource', CacheKey.ProductUseCaseToResource, params, options);

// SecurityUseCaseComponent <-> Resource (NEW)
const useFetchSecurityUseCaseComponentToResources = (params?, options?) =>
  useFetchItems('db/security-use-case-component-to-resource', CacheKey.GtmSolutionUseCaseComponentToResource, params, options);


// Tables (unchanged)
const useFetchTables = (params?: any, options?: any) => {
  const { data, isLoading, error } = useFetchItems('db/tables', CacheKey.Table, params, options);

  const models =
    Array.isArray(data?.models) && data.models.length > 0
      ? data.models
      : Array.isArray(data) && Array.isArray(data[0]?.models)
        ? data[0].models
        : Array.isArray(data?.raw) && Array.isArray(data.raw[0]?.models)
          ? data.raw[0].models
          : [];

  return {
    data: {
      models,
      raw: data,
    },
    isLoading,
    error,
  };
};

// ---- Mutations ----

// Custom Tile
const useAddCustomTileMutation = () =>
  useAddItemMutation('db/custom-tile', (payload) => [CacheKey.CustomTile]);

const useUpdateCustomTileMutation = () =>
  useUpdateItemMutation('db/custom-tile', (payload) => [CacheKey.CustomTile, payload.id]);

// Custom Tile Category
const useAddCustomTileCategoryMutation = () =>
  useAddItemMutation('db/custom-tile-category', (payload) => [CacheKey.CustomTileCategory]);

// Custom Tile View
const useAddCustomTileViewMutation = () =>
  useAddItemMutation('db/custom-tile-view', (payload) => [CacheKey.CustomTileView]);

const useAddResourceMutation = () =>
  useAddItemMutation('db/resource', () => [CacheKey.Resource]);
const useUpdateResourceMutation = () =>
  useUpdateItemMutation('db/resource', (payload) => [CacheKey.Resource, payload.id]);




// ProductComponent junction tables (NEW)

const useAddProductComponentToResourceMutation = () =>
  useAddItemMutation('db/product-component-to-resource', () => [CacheKey.ProductComponentToResource]);
const useUpdateProductComponentToResourceMutation = () =>
  useUpdateItemMutation('db/product-component-to-resource', (payload) => [CacheKey.ProductComponentToResource, payload.id]);

// ProductUseCase junction tables (NEW)

const useAddProductUseCaseToResourceMutation = () =>
  useAddItemMutation('db/product-use-case-to-resource', () => [CacheKey.ProductUseCaseToResource]);
const useUpdateProductUseCaseToResourceMutation = () =>
  useUpdateItemMutation('db/product-use-case-to-resource', (payload) => [CacheKey.ProductUseCaseToResource, payload.id]);


const useAddGoToMarketToResourceMutation = () =>
  useAddItemMutation('db/go-to-market-to-resource', () => [CacheKey.GtmSolutionToResource]);
const useUpdateGoToMarketToResourceMutation = () =>
  useUpdateItemMutation('db/go-to-market-to-resource', (payload) => [CacheKey.GtmSolutionToResource, payload.id]);

const useAddSolutionComponentToResourceMutation = () =>
  useAddItemMutation('db/solution-component-to-resource', () => [CacheKey.GtmSolutionComponentToResource]);
const useUpdateSolutionComponentToResourceMutation = () =>
  useUpdateItemMutation('db/solution-component-to-resource', (payload) => [CacheKey.GtmSolutionComponentToResource, payload.id]);

const useAddSecurityUseCaseToResourceMutation = () =>
  useAddItemMutation('db/security-use-case-to-resource', () => [CacheKey.GtmSolutionUseCaseToResource]);
const useUpdateSecurityUseCaseToResourceMutation = () =>
  useUpdateItemMutation('db/security-use-case-to-resource', (payload) => [CacheKey.GtmSolutionUseCaseToResource, payload.id]);

// SecurityUseCaseComponent junction tables (NEW)

const useAddSecurityUseCaseComponentToResourceMutation = () =>
  useAddItemMutation('db/security-use-case-component-to-resource', () => [CacheKey.GtmSolutionUseCaseComponentToResource]);
const useUpdateSecurityUseCaseComponentToResourceMutation = () =>
  useUpdateItemMutation('db/security-use-case-component-to-resource', (payload) => [CacheKey.GtmSolutionUseCaseComponentToResource, payload.id]);


const useFetchTileDataSources = (params?: any, options?: any) =>
  useFetchItems<TileDataSource[]>('db/tile-data-source', CacheKey.TileDataSource, params, options);

const useFetchTileDataSourceEntities = (params?: any, options?: any) =>
  useFetchItems<TileDataSourceEntity[]>('db/tile-data-source-entity', CacheKey.TileDataSourceEntity, params, options);

const useFetchTileDataSourcesDetailed = (params?: any, options?: any) => {
  const effectiveParams = {
    is_active: true,
    detailed: true,
    depth: 1,
    size: 1000,
    page: 1,
    'order-by': 'sort_order',
    ...(params || {}),
  };

  const cacheKey = [CacheKey.TileDataSourceDetailed, JSON.stringify(effectiveParams)];

  return useQuery<TileDataSource[]>({
    queryKey: cacheKey,
    queryFn: () => fetchItems('db/tile-data-source', effectiveParams),
    ...{
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        ...options,
      },
  });
};

/**
 * Synthetic, client-side `Campaign Delivery` tile source. It is NOT a `db/tile-data-source` row: the
 * builder resolves a campaign delivery's rows at render time via `resolveCampaignDeliveryData` keyed
 * only on the string label, so no backend row/deploy is needed to expose it in the source dropdown.
 * Campaign-agnostic — it selects a delivery id, never a specific campaign, so every campaign charts
 * through this one source. Appended to the DB-driven catalog so all catalog consumers see it.
 */
const CAMPAIGN_DELIVERY_TILE_SOURCE: TileDataSource = {
  id: -1,
  key: 'campaign_delivery',
  label: 'Campaign Delivery',
  source_type: 'campaign_delivery',
  is_active: true,
  dev_only: false,
  supports_entities: false,
  supports_custom_path: false,
  supports_params: false,
  supports_payload: false,
  default_method: 'GET',
  allowed_methods: ['GET'],
  sort_order: 9999,
  entities: [],
};

const useTileSourceCatalog = (options?: any) => {
  const query = useFetchTileDataSourcesDetailed({}, options);

  const dataSources = useMemo(() => {
    const items = query?.data ?? [];
    const filtered = items.filter((src: TileDataSource) => {
      if (process.env.NODE_ENV !== 'development' && src.dev_only) {
        return false;
      }
      return true;
    });
    if (!filtered.some((src: TileDataSource) => src.label === CAMPAIGN_DELIVERY_TILE_SOURCE.label)) {
      filtered.push(CAMPAIGN_DELIVERY_TILE_SOURCE);
    }
    return filtered;
  }, [query?.data]);

  const sourcesForSelect = useMemo(
    () =>
      dataSources.map((src: TileDataSource) => ({
        value: src.label,
        label: src.label,
        key: src.key,
        id: src.id,
      })),
    [dataSources]
  );

  const sourceMapByLabel = useMemo(() => {
    const map: Record<string, TileDataSource> = {};
    dataSources.forEach((src: TileDataSource) => {
      map[src.label] = src;
    });
    return map;
  }, [dataSources]);

  const sourceMapByKey = useMemo(() => {
    const map: Record<string, TileDataSource> = {};
    dataSources.forEach((src: TileDataSource) => {
      map[src.key] = src;
    });
    return map;
  }, [dataSources]);

  return {
    ...query,
    dataSources,
    sourcesForSelect,
    sourceMapByLabel,
    sourceMapByKey,
  };
};

export {
  // Fetches (existing)
  useFetchSingleCustomTile,
  useFetchCustomTiles,
  useFetchIntegrationModules,
  useFetchTables,

  useFetchCustomTileCategories,

  useFetchCustomTileViews,

  useFetchVendors,

  // NEW: SecurityUseCaseComponent junction tables
  useFetchSecurityUseCaseComponentToResources,


  // Mutations (existing)
  useAddCustomTileMutation,
  useUpdateCustomTileMutation,

  useAddCustomTileCategoryMutation,

  useFetchDashboardPlacements,
  useFetchDashboardTileLinks,
  useFetchLeaderPages,
  useFetchSystemBanners,

  useAddCustomTileViewMutation,


  // NEW: ProductComponent junction tables
  useFetchProductComponentToResources,

  // NEW: ProductUseCase junction tables
  useFetchProductUseCaseToResources,

  useAddResourceMutation,
  useUpdateResourceMutation,

  // NEW: ProductComponent junction mutations

  useAddProductComponentToResourceMutation,
  useUpdateProductComponentToResourceMutation,

  // NEW: ProductUseCase junction mutations
  useAddProductUseCaseToResourceMutation,
  useUpdateProductUseCaseToResourceMutation,

  useAddGoToMarketToResourceMutation,
  useUpdateGoToMarketToResourceMutation,

  useAddSolutionComponentToResourceMutation,
  useUpdateSolutionComponentToResourceMutation,

  useAddSecurityUseCaseToResourceMutation,
  useUpdateSecurityUseCaseToResourceMutation,

  // NEW: SecurityUseCaseComponent junction mutations

  useAddSecurityUseCaseComponentToResourceMutation,
  useUpdateSecurityUseCaseComponentToResourceMutation,
  useFetchTileDataSources,
  useFetchTileDataSourceEntities,
  useFetchTileDataSourcesDetailed,
  useTileSourceCatalog,
};